/**
 * Practice Service for handling all practice mode related API calls
 */

import { createAbortSafeErrorHandler, defaultErrorHandler as commonErrorHandler, createHeaders, getApiUrls } from './serviceConfig';

/**
 * Creates a configured practice service
 * @param {Object} config - Configuration options
 * @param {Function} config.onError - Global error handler
 * @returns {Object} The practice service API
 */
export function createPracticeService(config = {}) {
  // Get configuration functions
  const configuredErrorHandler = config.onError || ((error) => commonErrorHandler('Practice service', error));

  const defaultErrorHandler = createAbortSafeErrorHandler(configuredErrorHandler);

  /**
   * Generate a new practice activity
   * @param {string} seedName - Optional seed name for activity generation
   * @returns {Promise<Object>} The generated activity data
   */
  const generateActivity = async (seedName = '') => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.generate(), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ seedName }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate activity: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Generate a new practice session with multiple activities
   * @param {Object} options - Session generation options
   * @param {string} options.discipline - The discipline for the session (e.g., "Matemáticas")
   * @param {number} options.maxActivityNumber - Maximum number of activities in the session
   * @param {string} options.lessonTitle - The lesson title in HTML format
   * @returns {Promise<Object>} The generated session data
   */
  const generateSession = async (options = {}) => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.generateSession(), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify(options),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate session: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get session information including all activity instances
   * @param {string} sessionId - The session ID
   * @returns {Promise<Object>} The session data with activity instances
   */
  const getSession = async (sessionId) => {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(`${urls.activities.getSessions()}/${sessionId}`, {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get session: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Discard a practice session the student created (idempotent).
   * Excluded from the sessions list and NOT_FOUND on read/resume server-side.
   * Responds 204 No Content, so resolving without a value means success.
   * @param {string} sessionId - The session ID
   * @returns {Promise<void>}
   */
  const discardSession = async (sessionId) => {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.discardSession(sessionId), {
        method: 'PATCH',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to discard session: ${response.status}`);
      }
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get a specific activity instance
   * @param {string} sessionId - The session ID
   * @param {string} activityInstanceId - The activity instance ID
   * @returns {Promise<Object>} The activity instance data
   */
  const getActivityInstance = async (sessionId, activityInstanceId) => {
    if (!sessionId || !activityInstanceId) {
      throw new Error('Session ID and Activity instance ID are required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.getActivityInstance(sessionId, activityInstanceId), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get activity instance: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Save user response for an activity
   * @param {string} activityInstanceId - The activity instance ID
   * @param {Object} response - The user's response data
   * @returns {Promise<Object>} The API response
   */
  const saveUserResponse = async (activityInstanceId, response) => {
    if (!activityInstanceId) {
      throw new Error('Activity instance ID is required');
    }

    try {
      const urls = getApiUrls();
      const apiResponse = await fetch(urls.activities.save(activityInstanceId), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify(response),
      });

      if (!apiResponse.ok) {
        throw new Error(`Failed to save response: ${apiResponse.status}`);
      }

      // Return the response data if available
      const contentType = apiResponse.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await apiResponse.json();
      }

      return { success: true };
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  const startActivityInstance = (activityInstanceId) => {
    if (!activityInstanceId) return Promise.resolve();

    const urls = getApiUrls();
    return fetch(urls.activities.start(activityInstanceId), {
      method: 'POST',
      headers: createHeaders(),
    }).catch(() => {});
  };

  /**
   * Notify an error for an activity
   * @param {Object} errorData - The error report data
   * @param {string} errorData.userId - The user ID
   * @param {string} errorData.username - The username
   * @param {string} errorData.tenant - The tenant
   * @param {string} errorData.activityInstanceId - The activity instance ID
   * @param {string} errorData.activityId - The activity ID
   * @param {string} errorData.errorType - The selected error option
   * @param {string} errorData.description - The user's comments
   * @returns {Promise<Object>} The API response
   */
  const notifyError = async (errorData) => {
    const { userId, username, tenant, activityInstanceId, activityId, errorType, description } = errorData;

    if (!userId || !username || !tenant || !activityInstanceId || !errorType) {
      throw new Error('UserId, username, tenant, activityInstanceId, and errorType are required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(`${urls.activities.notifyError()}`, {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({
          userId,
          username,
          tenant,
          activityInstanceId,
          activityId,
          errorType,
          description: description || '',
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to notify error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Check whether the current user has any practice sessions
   * @param {Object} options - Options
   * @param {AbortSignal} options.signal - Optional signal to abort the request on unmount
   * @returns {Promise<Object>} The check result
   */
  const checkSessions = async ({ signal } = {}) => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.checkSessions(), {
        method: 'GET',
        headers: createHeaders(),
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to check sessions: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Get the list of practice sessions for the current user
   * @returns {Promise<Object>} The sessions list data
   */
  const getSessions = async () => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.getSessions(), {
        method: 'GET',
        headers: createHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to get sessions: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Generate a custom practice session from a student's selection
   * @param {Object} payload - Custom session generation payload
   * @param {string} payload.courseId - The course ID
   * @param {string[]} payload.lessonIds - The selected lesson IDs
   * @param {string[]} payload.unitIds - The selected unit IDs
   * @returns {Promise<Object>} The generated custom session data
   */
  const generateCustomSession = async (payload) => {
    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.generateCustomSession(), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate custom session: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  /**
   * Generate a volatile practice session for a single blocked KC ("Áreas de mejora").
   * The session is not persisted as a recommendation, so it never appears in "Hoy":
   * it exists only while the student plays it.
   * @param {Object} payload - KC session generation payload
   * @param {string} payload.courseId - The course the KC is practised in
   * @param {string} payload.kcId - The blocked KC to practise
   * @returns {Promise<Object>} The generated KC session data
   */
  const generateKcSession = async (payload) => {
    if (!payload?.courseId || !payload?.kcId) {
      throw new Error('Course ID and KC ID are required');
    }

    try {
      const urls = getApiUrls();
      const response = await fetch(urls.activities.generateKcSession(), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ courseId: payload.courseId, kcId: payload.kcId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate KC session: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      defaultErrorHandler(error);
      throw error;
    }
  };

  // Return the service API
  return {
    generateActivity,
    generateSession,
    getSession,
    discardSession,
    getActivityInstance,
    saveUserResponse,
    startActivityInstance,
    notifyError,
    checkSessions,
    getSessions,
    generateCustomSession,
    generateKcSession,
  };
}

// Default export with default configuration
export default createPracticeService();
